/**
 * Runtime feature toggles — the resolution layer over the build-time env flags.
 *
 * Hamzah wanted a runtime platform on/off switch he can flip himself in
 * super-admin, instantly, with no redeploy. The existing per-feature flags
 * (VITE_ENABLE_HELP_CENTER in features/help/flags.ts and the light-category
 * VITE_ENABLE_*_LIGHTING flags in features/mockups/layoutFlags.ts) are inlined
 * at build time, so flipping one means a rebuild.
 *
 * This module composes a runtime toggle OVER each of those env flags. The env
 * flag becomes the DEFAULT: `resolved = runtimeToggle ?? envFlagDefault`. The
 * runtime value lives in the `system_settings` table (super-admin flipped, same
 * mechanism as `mobile_app_live`), so an absent or malformed row falls back to
 * the env default.
 *
 * This file is the PURE core (no React, no network) so the resolution is unit
 * testable; the hooks that fetch and the gate that redirects live alongside it.
 */
import { HELP_CENTER_ENABLED } from '@/features/help/flags';
import { LIGHT_CATEGORY_FLAGS } from '@/features/mockups/layoutFlags';
import type { LightCategoryFlags } from '@/features/mockups/utils/mockupLightCategories';

export type FeatureToggleName =
  | 'help_center'
  | 'landscape_lighting'
  | 'holiday_lighting'
  | 'permanent_lighting';

/**
 * The EXACT `system_settings.setting_key` values, seeded by
 * 20260827210720_feature_runtime_toggles.sql. A sibling lane depends on this
 * convention, so these strings are a contract — do not rename.
 *
 * Each row's `setting_value` is `{ "enabled": boolean }` (the mobile_app_live
 * shape minus its `platform` field — a pure on/off switch needs only `enabled`).
 */
export const FEATURE_TOGGLE_SETTING_KEYS = {
  help_center: 'feature_help_center_enabled',
  landscape_lighting: 'feature_landscape_lighting_enabled',
  holiday_lighting: 'feature_holiday_lighting_enabled',
  permanent_lighting: 'feature_permanent_lighting_enabled',
} as const satisfies Record<FeatureToggleName, string>;

export type FeatureToggleSettingKey =
  (typeof FEATURE_TOGGLE_SETTING_KEYS)[FeatureToggleName];

/** All four setting keys, for a single `.in(...)` read. */
export const FEATURE_TOGGLE_SETTING_KEY_LIST = Object.values(
  FEATURE_TOGGLE_SETTING_KEYS,
) as FeatureToggleSettingKey[];

/**
 * Build-time env default per feature — the fallback used when the runtime row is
 * absent, still loading, or unreadable. These ARE the existing env flags, so the
 * env flag keeps working exactly as documented; runtime only composes over it.
 */
export const FEATURE_TOGGLE_ENV_DEFAULTS: Record<FeatureToggleName, boolean> = {
  help_center: HELP_CENTER_ENABLED,
  landscape_lighting: LIGHT_CATEGORY_FLAGS.landscape,
  holiday_lighting: LIGHT_CATEGORY_FLAGS.holiday,
  permanent_lighting: LIGHT_CATEGORY_FLAGS.permanent,
};

/**
 * Runtime values parsed from system_settings. Only PRESENT rows appear — an
 * absent key is left undefined so the resolver falls through to the env default.
 * This is deliberately distinct from a present `{ enabled: false }`, which is a
 * real, super-admin-set OFF.
 */
export type FeatureToggleRuntimeMap = Partial<Record<FeatureToggleName, boolean>>;

/** setting_key -> FeatureToggleName (reverse of FEATURE_TOGGLE_SETTING_KEYS). */
const NAME_BY_KEY = Object.fromEntries(
  Object.entries(FEATURE_TOGGLE_SETTING_KEYS).map(
    ([name, key]) => [key, name as FeatureToggleName],
  ),
) as Record<string, FeatureToggleName>;

interface SystemSettingRow {
  setting_key: string;
  setting_value: unknown;
}

/**
 * Parse raw system_settings rows into a runtime map. A row contributes its
 * boolean only when present; unknown keys are ignored and absent keys are
 * omitted (so the resolver uses the env default for them).
 */
export function parseFeatureToggleRows(
  rows: SystemSettingRow[] | null | undefined,
): FeatureToggleRuntimeMap {
  const map: FeatureToggleRuntimeMap = {};
  for (const row of rows ?? []) {
    const name = NAME_BY_KEY[row.setting_key];
    if (!name) continue;
    const value = row.setting_value as { enabled?: unknown } | null;
    if (typeof value?.enabled === 'boolean') {
      map[name] = value.enabled;
    }
  }
  return map;
}

/** Resolve one feature: the runtime value when present, else the env default. */
export function resolveFeatureToggle(
  name: FeatureToggleName,
  runtime: FeatureToggleRuntimeMap,
): boolean {
  return runtime[name] ?? FEATURE_TOGGLE_ENV_DEFAULTS[name];
}

/**
 * Resolve the three light categories into a `LIGHT_CATEGORY_FLAGS`-shaped object
 * the mockup editor can hand to its toolbar + creation boundary. Renderers are
 * never given this object — saved runs always render regardless of the toggles
 * (guarded by layoutFlags.test.ts).
 */
export function resolveLightCategoryFlags(
  runtime: FeatureToggleRuntimeMap,
): LightCategoryFlags {
  return {
    holiday: resolveFeatureToggle('holiday_lighting', runtime),
    permanent: resolveFeatureToggle('permanent_lighting', runtime),
    landscape: resolveFeatureToggle('landscape_lighting', runtime),
  };
}
